import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, Save, Building2, Landmark, FileText, Palette } from "lucide-react";
import { toast } from "sonner";
import GstAutomationPanel from "@/components/GstAutomationPanel";
import BrandingSettings from "@/components/khata/BrandingSettings";

function absLogo(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (url.startsWith("/api/")) return `${base}${url}`;
  return url;
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);
  const qrRef = useRef(null);
  const [qrBusy, setQrBusy] = useState(false);
  const decodeQr = async (f) => {
    if (!f) return;
    setQrBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/settings/upi-qr/decode", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((cur) => ({ ...cur, upi_qr_payload: data.payload, upi_id: data.upi_id || cur.upi_id }));
      toast.success(`${data.is_merchant ? "Merchant" : "UPI"} QR decoded`, { description: `${data.payee_name || ""} · ${data.upi_id} — click Save to apply` });
    } catch (e) { toast.error("Could not decode QR", { description: e.response?.data?.detail || e.message }); }
    finally { setQrBusy(false); if (qrRef.current) qrRef.current.value = ""; }
  };

  const load = async () => setS((await api.get("/settings/company")).data);
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/company", {
        company_name: s.company_name, tagline: s.tagline,
        address_line1: s.address_line1, address_line2: s.address_line2,
        city: s.city, state: s.state, state_code: s.state_code, pincode: s.pincode, country: s.country,
        gstin: s.gstin, pan: s.pan, phone: s.phone, email: s.email, website: s.website,
        logo_url: s.logo_url,
        bank_name: s.bank_name, bank_account: s.bank_account, bank_ifsc: s.bank_ifsc,
        bank_branch: s.bank_branch, bank_account_holder: s.bank_account_holder, upi_id: s.upi_id, upi_qr_payload: s.upi_qr_payload,
        invoice_prefix: s.invoice_prefix, invoice_notes: s.invoice_notes, invoice_terms: s.invoice_terms,
        primary_color: s.primary_color, default_gst_rate: parseFloat(s.default_gst_rate),
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(data);
      toast.success("Logo uploaded");
    } catch (e) { toast.error("Upload failed", { description: e.response?.data?.detail || e.message }); }
    finally { setLogoBusy(false); }
  };

  if (!s) return <div className="text-sm text-zinc-500">Loading settings…</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Customisation</div>
          <h1 className="font-heading font-black tracking-tight text-4xl mt-2">Settings</h1>
          <p className="text-sm text-zinc-600 mt-2">Logo, company details, bank account and invoice defaults — used on every generated invoice PDF.</p>
        </div>
        <Button data-testid="settings-save-btn" onClick={save} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
          <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save settings"}
        </Button>
      </header>

      <Section icon={Building2} title="Company">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1 flex flex-col items-center gap-3 border border-zinc-200 p-4 bg-white">
            <div className="w-24 h-24 border border-zinc-200 bg-zinc-50 grid place-items-center overflow-hidden">
              {s.logo_url ? <img src={absLogo(s.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-zinc-400 tracking-widest uppercase">No logo</span>}
            </div>
            <input ref={fileRef} type="file" data-testid="logo-file-input" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <Button data-testid="logo-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={logoBusy} className="rounded-none border-zinc-300 w-full">
              <UploadCloud size={14} className="mr-1.5" /> {logoBusy ? "Uploading…" : "Upload logo"}
            </Button>
            <div className="text-[10px] text-zinc-500 text-center">PNG / JPG / WEBP · ≤ 3 MB</div>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <Field label="Company name" v={s.company_name} onChange={upd("company_name")} tid="s-company-name" full />
            <Field label="Tagline" v={s.tagline} onChange={upd("tagline")} tid="s-tagline" full />
            <Field label="GSTIN" v={s.gstin} onChange={upd("gstin")} tid="s-gstin" />
            <Field label="PAN" v={s.pan} onChange={upd("pan")} tid="s-pan" />
            <Field label="Phone" v={s.phone} onChange={upd("phone")} tid="s-phone" />
            <Field label="Email" v={s.email} onChange={upd("email")} tid="s-email" />
            <Field label="Website" v={s.website} onChange={upd("website")} tid="s-website" full />
          </div>
        </div>
      </Section>

      <Section icon={Building2} title="Registered address">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Address line 1" v={s.address_line1} onChange={upd("address_line1")} full />
          <Field label="Address line 2" v={s.address_line2} onChange={upd("address_line2")} full />
          <Field label="City" v={s.city} onChange={upd("city")} />
          <Field label="State" v={s.state} onChange={upd("state")} />
          <Field label="State code" v={s.state_code} onChange={upd("state_code")} />
          <Field label="Pincode" v={s.pincode} onChange={upd("pincode")} />
          <Field label="Country" v={s.country} onChange={upd("country")} />
        </div>
      </Section>

      <Section icon={Landmark} title="Bank details (printed on invoice)">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Account holder" v={s.bank_account_holder} onChange={upd("bank_account_holder")} full />
          <Field label="Bank name" v={s.bank_name} onChange={upd("bank_name")} tid="s-bank" />
          <Field label="Branch" v={s.bank_branch} onChange={upd("bank_branch")} />
          <Field label="Account number" v={s.bank_account} onChange={upd("bank_account")} tid="s-bank-ac" />
          <Field label="IFSC" v={s.bank_ifsc} onChange={upd("bank_ifsc")} tid="s-bank-ifsc" />
          <Field label="UPI ID" v={s.upi_id} onChange={upd("upi_id")} tid="s-upi" />
          <div className="sm:col-span-2">
            <div className="flex items-end gap-2">
              <div className="flex-1"><Field label="Merchant QR payload (optional)" v={s.upi_qr_payload} onChange={upd("upi_qr_payload")} tid="s-upi-qr" /></div>
              <input ref={qrRef} type="file" accept="image/*" className="hidden" data-testid="upi-qr-file-input" onChange={(e) => decodeQr(e.target.files?.[0])} />
              <Button type="button" data-testid="upi-qr-upload-btn" variant="outline" onClick={() => qrRef.current?.click()} disabled={qrBusy} className="rounded-none border-zinc-300 mb-[1px]"><UploadCloud size={14} className="mr-1.5" /> {qrBusy ? "Decoding…" : "Upload QR image"}</Button>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Upload a photo/screenshot of your Google Pay Business / PhonePe Business / Paytm merchant QR and it is decoded automatically (or paste the <code>upi://pay?…</code> text). Khata reminders then reuse your merchant QR with the exact amount added, so every UPI app accepts it.</div>
          </div>
        </div>
      </Section>

      <Section icon={FileText} title="Invoice defaults">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Number prefix" v={s.invoice_prefix} onChange={upd("invoice_prefix")} tid="s-prefix" />
          <Field label="Default GST %" v={s.default_gst_rate} onChange={upd("default_gst_rate")} tid="s-gst-rate" />
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Primary color</Label>
            <div className="flex gap-2 items-center">
              <input data-testid="s-color" type="color" value={s.primary_color || "#09090B"} onChange={(e) => setS({ ...s, primary_color: e.target.value })} className="h-9 w-14 border border-zinc-300" />
              <Input value={s.primary_color || ""} onChange={upd("primary_color")} />
            </div>
          </div>
          <div className="col-span-3">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Footer notes</Label>
            <Textarea data-testid="s-notes" rows={2} value={s.invoice_notes || ""} onChange={upd("invoice_notes")} />
          </div>
          <div className="col-span-3">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Terms &amp; conditions</Label>
            <Textarea data-testid="s-terms" rows={4} value={s.invoice_terms || ""} onChange={upd("invoice_terms")} />
          </div>
        </div>
      </Section>

      <BrandingSettings settingsHint="Bank details above" />

      <GstAutomationPanel />

      <Section icon={Palette} title="Integrations">
        <div className="text-sm text-zinc-700 space-y-3">
          <div>Email · <span className="font-semibold text-emerald-700">Ready</span> (Emergent-managed Resend)</div>
          <div>WhatsApp · {s.twilio_configured ? <span className="font-semibold text-emerald-700">Ready (Twilio)</span> : <span className="text-amber-700">wa.me click-to-chat fallback (no Twilio keys yet)</span>}</div>
          <div>SMS · {s.twilio_configured ? <span className="font-semibold text-emerald-700">Ready (Twilio)</span> : <span className="text-rose-700">Not configured — add TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_SMS_FROM to /app/backend/.env</span>}</div>

          <TwilioTester configured={s.twilio_configured} />

          {!s.twilio_configured ? (
            <div className="mt-2 border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 space-y-1">
              <div className="font-semibold text-zinc-900">How to enable Twilio</div>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Open <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline">console.twilio.com</a> and copy your Account SID + Auth Token.</li>
                <li>Add these 4 lines to <code className="font-mono">/app/backend/.env</code> and restart the backend:
                  <pre className="bg-white border border-zinc-200 mt-1 p-2 tabular text-[11px] overflow-x-auto">TWILIO_ACCOUNT_SID=AC...
TWILIO_AUTH_TOKEN=...
TWILIO_SMS_FROM=+1...
TWILIO_WHATSAPP_FROM=+1...</pre>
                </li>
                <li>Come back here and use the tester above to send a one-off SMS / WhatsApp.</li>
              </ol>
            </div>
          ) : null}
        </div>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="bg-white border border-zinc-200 p-6">
      <header className="flex items-center gap-3 pb-3 mb-4 border-b border-zinc-200">
        <Icon size={16} strokeWidth={1.75} />
        <h2 className="font-heading font-bold tracking-tight text-lg">{title}</h2>
      </header>
      {children}
    </section>
  );
}

function Field({ label, v, onChange, tid, full }) {
  return (
    <div className={full ? "col-span-3" : ""}>
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">{label}</Label>
      <Input data-testid={tid} value={v ?? ""} onChange={onChange} />
    </div>
  );
}

function TwilioTester({ configured }) {
  const [channel, setChannel] = React.useState("sms");
  const [to, setTo] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient number in E.164 format (+CountryCode…)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/twilio-test", { channel, to: to.trim(), message: msg || undefined });
      if (data.ok) toast.success(`${channel.toUpperCase()} test sent`, { description: `SID ${data.sid || ""} · ${data.status || "queued"}` });
      else toast.error("Test failed", { description: data.error || "Twilio rejected the request" });
    } catch (e) {
      toast.error("Not configured", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border border-zinc-200 bg-white p-3">
      <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-2">Send a Twilio test message</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[110px_160px_1fr_120px] gap-2 items-end">
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase font-semibold">Channel</Label>
          <select data-testid="twilio-test-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full h-9 border border-zinc-300 px-2 text-sm">
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase font-semibold">To (E.164)</Label>
          <Input data-testid="twilio-test-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+919812345678" />
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label className="text-[10px] tracking-[0.14em] uppercase font-semibold">Message (optional)</Label>
          <Input data-testid="twilio-test-msg" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Leave blank for default" />
        </div>
        <Button data-testid="twilio-test-send-btn" onClick={send} disabled={busy || !configured} className="sm:col-span-2 lg:col-span-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
          {busy ? "Sending…" : "Send test"}
        </Button>
      </div>
      {!configured ? <div className="text-[11px] text-amber-700 mt-2">Add Twilio keys to enable this button.</div> : null}
    </div>
  );
}
